import { faArrowUp, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';

import { useUpdateOrganization } from '@/hooks/useDugoutOrganizations';
import type { OrganizationDetail, OrganizationPayload } from '@/types/organization';
import { Button, Input, InputGroup, labelVariants, Pill, Textarea, Toggle } from '@/ui';
import { Alert } from '@/ui/Alert';

import type { OrganizationFormValues, Props } from './types';

// Loose shape check only -- the server validates addresses properly.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const buildDefaultValues = (organization: OrganizationDetail): OrganizationFormValues => ({
  name: organization.name,
  description: organization.description ?? '',
  internalOwner: organization.internal_owner ?? '',
  // A null visibility reads as hidden, as it does on the detail page and in
  // the public pickers, so editing the org doesn't publish it.
  isVisibleToPublic: !!organization.is_visible_to_public,
  emails: organization.emails.map((address) => ({ address })),
  hasReferrerConfig: !!organization.referrer_config,
  referrerIsActive: organization.referrer_config?.is_active ?? true,
  // A new code only auto-generates ROIs if there's an email to send them to.
  referrerAutoGenerateRoi:
    organization.referrer_config?.should_auto_generate_roi ?? organization.emails.length > 0
});

const toPayload = (values: OrganizationFormValues): OrganizationPayload => ({
  name: values.name.trim(),
  description: values.description.trim() || null,
  internal_owner: values.internalOwner.trim() || null,
  is_visible_to_public: values.isVisibleToPublic,
  emails: values.emails.map((email) => email.address.trim().toLowerCase()).filter(Boolean),
  ...(values.hasReferrerConfig && {
    referrer_config: {
      is_active: values.referrerIsActive,
      should_auto_generate_roi: values.referrerAutoGenerateRoi
    }
  })
});

const OrganizationForm = ({ organization, onCancel, onSuccess }: Props) => {
  // ROI requests go to the default email and fail without one, so an org that
  // has an email can't be left with none (the server enforces this too).
  const mustKeepAnEmail = organization.emails.length > 0;

  const updateOrganization = useUpdateOrganization();
  const isPending = updateOrganization.isPending;

  const {
    control,
    formState: { errors },
    handleSubmit
  } = useForm<OrganizationFormValues>({ defaultValues: buildDefaultValues(organization) });
  const emails = useFieldArray({ control, name: 'emails' });
  const [hasReferrerConfig, referrerAutoGenerateRoi] = useWatch({
    control,
    name: ['hasReferrerConfig', 'referrerAutoGenerateRoi']
  });
  const existingReferrerConfig = organization.referrer_config;

  const onSubmit = handleSubmit((values) => {
    updateOrganization.mutate({ id: organization.id, data: toPayload(values) }, { onSuccess });
  });

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {organization.is_kustomer_managed && (
        <Alert
          message="Synced from Kustomer. Kustomer's company sync can overwrite the name, visibility, internal owner, emails and auto-generate ROIs setting when the company changes there."
          variant="info"
        />
      )}

      <InputGroup>
        <Controller
          name="name"
          control={control}
          rules={{ required: 'Name is required' }}
          render={({ field }) => (
            <Input {...field} id="organization-name" label="Name" error={errors.name?.message} />
          )}
        />
      </InputGroup>

      <InputGroup>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <Textarea {...field} id="organization-description" label="Description" rows={3} />
          )}
        />
      </InputGroup>

      <InputGroup>
        <Controller
          name="internalOwner"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id="organization-internal-owner"
              label="Internal owner"
              helpText="Who at Affect owns this relationship."
            />
          )}
        />
      </InputGroup>

      <InputGroup>
        <Controller
          name="isVisibleToPublic"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Toggle checked={value} onChange={onChange} aria-label="Visible to public">
              Visible to public
            </Toggle>
          )}
        />
        <p className="text-xs text-uniform-gray-600 dark:text-uniform-gray-400">
          Hidden organizations can&apos;t be picked as an ROI recipient or referral source.
        </p>
      </InputGroup>

      <fieldset className="space-y-2">
        <legend className={labelVariants()}>Emails</legend>
        <p className="text-xs text-uniform-gray-600 dark:text-uniform-gray-400">
          ROI requests are sent to the default email.
        </p>
        {emails.fields.map((item, index) => (
          <div key={item.id} className="flex items-start gap-2">
            <div className="flex-1">
              <Controller
                name={`emails.${index}.address`}
                control={control}
                rules={{
                  required: 'Enter an email or remove this row',
                  pattern: { value: EMAIL_PATTERN, message: 'Enter a valid email' }
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    id={`organization-email-${index}`}
                    type="email"
                    label={`Email ${index + 1}`}
                    hideLabel
                    error={errors.emails?.[index]?.address?.message}
                  />
                )}
              />
            </div>
            {index === 0 ? (
              <Pill size="sm" variant="subtle" color="primary" className="mt-2">
                Default
              </Pill>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Make email ${index + 1} the default`}
                onClick={() => emails.move(index, 0)}>
                <FontAwesomeIcon icon={faArrowUp} /> Make default
              </Button>
            )}
            {!(mustKeepAnEmail && emails.fields.length === 1) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Remove email ${index + 1}`}
                onClick={() => emails.remove(index)}>
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            )}
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => emails.append({ address: '' })}>
          Add email
        </Button>
        {emails.fields.length === 0 && (
          <Alert
            message="Without an email, ROIs can't be requested for this organization and none will be generated automatically."
            variant="warning"
          />
        )}
      </fieldset>

      <fieldset className="space-y-2">
        <legend className={labelVariants()}>Referral code</legend>
        {existingReferrerConfig ? (
          <p className="text-sm">
            Code <span className="font-mono">{existingReferrerConfig.code}</span>
          </p>
        ) : (
          <Controller
            name="hasReferrerConfig"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Toggle checked={value} onChange={onChange} aria-label="Issue a referral code">
                Issue a referral code
              </Toggle>
            )}
          />
        )}
        {hasReferrerConfig && (
          <>
            <Controller
              name="referrerIsActive"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Toggle checked={value} onChange={onChange} aria-label="Referral code active">
                  Referral code active
                </Toggle>
              )}
            />
            <Controller
              name="referrerAutoGenerateRoi"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Toggle checked={value} onChange={onChange} aria-label="Auto-generate ROIs">
                  Auto-generate ROIs
                </Toggle>
              )}
            />
            <p className="text-xs text-uniform-gray-600 dark:text-uniform-gray-400">
              When on, referrals from this organization automatically request an ROI to its default
              email.
            </p>
            {referrerAutoGenerateRoi && emails.fields.length === 0 && (
              <Alert
                message="Auto-generated ROIs need a default email. Until you add one above, no ROIs will be generated for referrals from this organization."
                variant="warning"
              />
            )}
          </>
        )}
      </fieldset>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={isPending}>
          Save changes
        </Button>
      </div>
    </form>
  );
};

export default OrganizationForm;
